import React, { useState, useEffect } from 'react';
import { dashboardApi, alertsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Package, Warehouse, AlertTriangle, TrendingUp, 
  ArrowUpRight, ArrowDownRight, Clock, Bell,
  Box, Truck, ClipboardList, BarChart3, Euro
} from 'lucide-react';
import { RadialBarChart, RadialBar, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from 'recharts';

const STORAGE_CLASSES = ['1.1G', '1.2G', '1.3G', '1.4G', '1.4S'];  // Classes ADR pour stockage/timbrage

const STORAGE_CLASS_COLORS = {
  '1.1G': '#ef4444',  // Rouge - plus dangereux
  '1.2G': '#f97316',  // Orange
  '1.3G': '#f59e0b',  // Amber
  '1.4G': '#22c55e',  // Vert
  '1.4S': '#3b82f6',  // Bleu
};

const CATEGORY_COLORS = {
  F1: '#22c55e',
  F2: '#3b82f6',
  F3: '#f59e0b',
  F4: '#ef4444',
  T1: '#a855f7',
  T2: '#ec4899',
  P1: '#22d3ee',
  P2: '#14b8a6',
};

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, alertsRes] = await Promise.all([
        dashboardApi.getStats(),
        alertsApi.getAll({ is_read: false })
      ]);
      setStats(statsRes.data);
      setAlerts(alertsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatMovementType = (type) => {
    const types = {
      entry: 'Entrée',
      exit: 'Sortie',
      transfer: 'Transfert',
      adjustment: 'Ajustement',
      return: 'Retour',
      consumption: 'Consommation'
    };
    return types[type] || type;
  };

  const getMovementIcon = (type) => {
    switch (type) {
      case 'entry': return <ArrowDownRight className="w-4 h-4 text-green-400" />;
      case 'exit': return <ArrowUpRight className="w-4 h-4 text-red-400" />;
      case 'transfer': return <Truck className="w-4 h-4 text-blue-400" />;
      case 'return': return <ArrowDownRight className="w-4 h-4 text-purple-400" />;
      default: return <Box className="w-4 h-4 text-zinc-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const categoryData = Object.entries(stats?.stock_by_category || {}).map(([cat, data]) => ({
    name: cat,
    quantity: data.quantity,
    nec: data.nec,
    fill: CATEGORY_COLORS[cat] || '#71717a'
  }));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800 card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">Produits Actifs</p>
                <p className="text-3xl font-heading font-bold text-white mt-1">
                  {stats?.total_products || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Package className="w-6 h-6 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800 card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">Stock Total</p>
                <p className="text-3xl font-heading font-bold text-white mt-1">
                  {stats?.total_stock?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-zinc-500 mt-1">unités</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                <Box className="w-6 h-6 text-cyan-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800 card-hover border-emerald-500/20" data-testid="stock-valuation-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">Valorisation Stock</p>
                <p className="text-2xl font-heading font-bold text-white mt-1">
                  {stats?.stock_valuation_ht != null 
                    ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(stats.stock_valuation_ht) 
                    : '—'}
                </p>
                <p className="text-xs text-zinc-500 mt-1">HT (prix d'achat)</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <Euro className="w-6 h-6 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800 card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">Préparations en cours</p>
                <p className="text-3xl font-heading font-bold text-white mt-1">
                  {stats?.pending_preparations || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-fuchsia-500/20 flex items-center justify-center">
                <ClipboardList className="w-6 h-6 text-fuchsia-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`bg-zinc-900/50 border-zinc-800 card-hover ${stats?.critical_alerts > 0 ? 'border-red-500/50' : ''}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">Alertes Actives</p>
                <p className="text-3xl font-heading font-bold text-white mt-1">
                  {stats?.active_alerts || 0}
                </p>
                {stats?.critical_alerts > 0 && (
                  <p className="text-xs text-red-400 mt-1">{stats.critical_alerts} critiques</p>
                )}
              </div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                stats?.critical_alerts > 0 ? 'bg-red-500/20' : 'bg-green-500/20'
              }`}>
                <AlertTriangle className={`w-6 h-6 ${
                  stats?.critical_alerts > 0 ? 'text-red-400' : 'text-green-400'
                }`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Depot Capacity Gauges */}
        <Card className="lg:col-span-2 bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
              <Warehouse className="w-5 h-5 text-amber-400" />
              Capacités des Dépôts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.depot_stats?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {stats.depot_stats.map((depot) => (
                  <div key={depot.id} className="bg-zinc-950/50 rounded-xl p-4 border border-zinc-800">
                    <h4 className="font-semibold text-white mb-4">{depot.name}</h4>
                    <div className="grid grid-cols-5 gap-2">
                      {STORAGE_CLASSES.map((cls) => {
                        const capacity = depot.capacities?.[cls] || 0;
                        const usage = depot.current_usage?.[cls]?.nec || 0;
                        const percent = capacity > 0 ? (usage / capacity) * 100 : 0;
                        const color = percent >= 100 ? '#ef4444' : percent >= depot.alert_threshold ? '#f59e0b' : STORAGE_CLASS_COLORS[cls] || '#22c55e';
                        
                        if (capacity === 0) return null;
                        
                        return (
                          <div key={cls} className="text-center">
                            <div className="relative w-14 h-14 mx-auto">
                              <svg className="w-full h-full -rotate-90">
                                <circle
                                  cx="28"
                                  cy="28"
                                  r="24"
                                  fill="none"
                                  stroke="#27272a"
                                  strokeWidth="4"
                                />
                                <circle
                                  cx="28"
                                  cy="28"
                                  r="24"
                                  fill="none"
                                  stroke={color}
                                  strokeWidth="4"
                                  strokeLinecap="round"
                                  strokeDasharray={`${percent * 1.5} 150`}
                                  style={{ filter: `drop-shadow(0 0 4px ${color})` }}
                                />
                              </svg>
                              <span className="absolute inset-0 flex items-center justify-center text-xs font-mono text-white">
                                {Math.round(percent)}%
                              </span>
                            </div>
                            <Badge 
                              variant="outline" 
                              className={`mt-2 text-xs ${
                                cls === '1.1G' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                                cls === '1.2G' ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' :
                                cls === '1.3G' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                                cls === '1.4G' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                                'bg-blue-500/20 text-blue-400 border-blue-500/30'
                              }`}
                            >
                              {cls}
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-500">
                <Warehouse className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Aucun dépôt configuré</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alerts Panel */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-400" />
                Alertes
              </CardTitle>
              {alerts.length > 0 && (
                <Badge variant="destructive" className="bg-red-500/20 text-red-400">
                  {alerts.length}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[280px]">
              {alerts.length > 0 ? (
                <div className="space-y-3">
                  {alerts.slice(0, 10).map((alert) => (
                    <div
                      key={alert.id}
                      className={`p-3 rounded-lg border ${
                        alert.severity === 'critical'
                          ? 'bg-red-500/10 border-red-500/30'
                          : alert.severity === 'warning'
                          ? 'bg-amber-500/10 border-amber-500/30'
                          : 'bg-blue-500/10 border-blue-500/30'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <AlertTriangle className={`w-4 h-4 mt-0.5 ${
                          alert.severity === 'critical' ? 'text-red-400' :
                          alert.severity === 'warning' ? 'text-amber-400' : 'text-blue-400'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{alert.message}</p>
                          <p className="text-xs text-zinc-500 mt-1">
                            {new Date(alert.created_at).toLocaleString('fr-FR')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-zinc-500">
                  <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucune alerte active</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Stock by Category */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-cyan-400" />
              Stock par Catégorie
            </CardTitle>
          </CardHeader>
          <CardContent>
            {categoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={categoryData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fill: '#71717a', fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#71717a', fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }}
                    labelStyle={{ color: '#fff' }}
                    itemStyle={{ color: '#a1a1aa' }}
                    formatter={(value, name) => [value.toLocaleString(), name === 'quantity' ? 'Quantite' : 'NEC (g)']}
                  />
                  <Bar dataKey="quantity" radius={[4, 4, 0, 0]}>
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-zinc-500">
                <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Aucune donnée de stock</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Movements */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-fuchsia-400" />
              Mouvements Récents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[250px]">
              {stats?.recent_movements?.length > 0 ? (
                <div className="space-y-3">
                  {stats.recent_movements.map((mov) => (
                    <div
                      key={mov.id}
                      className="flex items-center gap-3 p-3 rounded-lg bg-zinc-950/50 border border-zinc-800"
                    >
                      {getMovementIcon(mov.movement_type)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">
                          <span className="font-mono text-cyan-400">{mov.product_name || mov.product_id?.slice(0, 12)}</span>
                          {mov.product_designation && <span className="text-zinc-400 ml-2 text-xs">{mov.product_designation}</span>}
                        </p>
                        <p className="text-xs text-zinc-500">
                          {formatMovementType(mov.movement_type)} • {mov.quantity} unites
                          {mov.depot_name && <span> • {mov.depot_name}</span>}
                          {mov.to_depot_name && <span> → {mov.to_depot_name}</span>}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-zinc-400">
                          {new Date(mov.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <p className="text-xs text-zinc-500">{mov.user_name || 'Système'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-zinc-500">
                  <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucun mouvement récent</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Low Stock Products */}
      {stats?.low_stock_products?.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800 border-amber-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading text-amber-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Produits en Stock Bas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {stats.low_stock_products.map((product) => (
                <div
                  key={product.product_id}
                  className="flex items-center justify-between p-3 rounded-lg bg-amber-500/5 border border-amber-500/20"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-mono text-amber-400 truncate">{product.reference}</p>
                    <p className="text-xs text-zinc-400 truncate">{product.designation}</p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-lg font-bold text-white">{product.available}</p>
                    <p className="text-xs text-zinc-500">/ {product.threshold}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
